import { supabase } from '../lib/supabase'

/**
 * @file favoritesService.js
 * @description Supabase service for managing user favorites (artworks)
 *
 * NAMING CONVENTION:
 * - All params use snake_case (user_id, artwork_id)
 * - Aligned with database schema (favorites table)
 * - SELECT specific columns (NO SELECT *)
 */
export const favoritesService = {
  /**
   * Get user's favorite artworks
   * @param {string} user_id - User UUID
   * @returns {Promise<{data: Array|null, error: string|null}>}
   */
  async getUserFavorites(user_id) {
    try {
      // First, let's verify that this user has favorites
      const { data: directCheck, error: directError } = await supabase
        .from('favorites')
        .select('artwork_id, created_at')
        .eq('user_id', user_id);

      if (directError) {
        throw directError;
      }

      // Now try the full query with artwork data - being explicit about relationships
      const { data, error } = await supabase
        .from('favorites')
        .select(`
          user_id,
          artwork_id,
          created_at,
          artwork:artworks!artwork_id(
            id,
            title,
            price,
            images,
            artist_id,
            category_id,
            artist:users!artist_id(
              id,
              full_name
            )
          )
        `)
        .eq('user_id', user_id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  /**
   * Add artwork to favorites
   * @param {string} user_id - User UUID
   * @param {string} artwork_id - Artwork UUID
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  async addToFavorites(user_id, artwork_id) {
    try {
      const { data, error } = await supabase
        .from('favorites')
        .insert({
          user_id: user_id,
          artwork_id: artwork_id
        })
        .select()
        .single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  /**
   * Remove artwork from favorites
   * @param {string} user_id - User UUID
   * @param {string} artwork_id - Artwork UUID
   * @returns {Promise<{error: string|null}>}
   */
  async removeFromFavorites(user_id, artwork_id) {
    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user_id)
        .eq('artwork_id', artwork_id)

      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error.message }
    }
  },

  /**
   * Check if artwork is in favorites
   * @param {string} user_id - User UUID
   * @param {string} artwork_id - Artwork UUID
   * @returns {Promise<{isFavorite: boolean, error: string|null}>}
   */
  async isFavorite(user_id, artwork_id) {
    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('user_id, artwork_id, created_at')
        .eq('user_id', user_id)
        .eq('artwork_id', artwork_id)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') throw error
      return { isFavorite: !!data, error: null }
    } catch (error) {
      return { isFavorite: false, error: error.message }
    }
  }
}